import { useTranslation } from 'react-i18next'
import { Col, Row } from 'antd'
import clsx from 'clsx'
import Skeleton from 'react-loading-skeleton'

import FormatPrice from '@/components/FormatPrice'

import styles from './B2BecCartSummary.module.scss'

const columnSpans = {
  left: { xs: 14, sm: 14, md: 14, lg: 14, xl: 14 },
  right: { xs: 10, sm: 10, md: 10, lg: 10, xl: 10 },
}

interface RenderSubtitlesProps {
  title: string
  price: number
  currencyUnit: string
  bold?: boolean
  uppercase?: boolean
  isLoading?: boolean
  hidden?: boolean
}

const RenderSubtitles = ({
  title,
  price,
  currencyUnit,
  bold,
  uppercase,
  isLoading,
  hidden,
}: RenderSubtitlesProps) => {
  return (
    !hidden && (
      <Row className={clsx(bold && styles.bold, uppercase && styles.uppercase)}>
        <Col className={styles.subtitle} {...columnSpans.left}>
          <p className="mb-0">{title}</p>
        </Col>
        <Col className={styles.subtitle__value} {...columnSpans.right}>
          {isLoading ? <Skeleton /> : (
            <FormatPrice
              className="mb-0"
              currencyUnit={currencyUnit}
              price={price}
              wrappedBy="p"
            />
          )}
        </Col>
      </Row>
    )
  )
}

interface B2BecCartSummaryProps {
  cartSubTotal: number
  cartTotal: number
  shippingCosts: number
  voucherValue?: {
    code: string
    totalDiscount: number
  }
  additionalCost: number
  vat: number
  cartTotalVAT: number
  currencyUnit: string
  checkoutSettings?: {
    includingVAT: boolean
    hideVAT: boolean
  }
  isLoading?: boolean
  hideVAT?: boolean
  hideVoucher?: boolean
}

const B2BecCartSummary = ({
  cartSubTotal,
  cartTotal,
  shippingCosts,
  voucherValue,
  additionalCost,
  vat,
  cartTotalVAT,
  currencyUnit,
  checkoutSettings,
  isLoading,
  hideVAT,
  hideVoucher,
}: B2BecCartSummaryProps) => {
  const { t } = useTranslation()

  return (
    <div className={styles.B2BecCartSummary}>
      <div>
        <h3 className="cost-summary__title mb-0">
          {t('checkout.step3.upperSection.rightPart.title')}
        </h3>
        <RenderSubtitles
          currencyUnit={currencyUnit}
          isLoading={isLoading}
          price={cartSubTotal}
          title={t('checkout.step3.upperSection.rightPart.subtotal')}
        />
        <RenderSubtitles
          currencyUnit={currencyUnit}
          isLoading={isLoading}
          price={additionalCost}
          title={t('checkout.step3.upperSection.rightPart.additionalCost')}
        />
        <RenderSubtitles
          currencyUnit={currencyUnit}
          isLoading={isLoading}
          price={shippingCosts}
          title={t('checkout.step3.upperSection.rightPart.shippingCosts')}
        />
        <RenderSubtitles
          bold
          currencyUnit={currencyUnit}
          isLoading={isLoading}
          price={cartTotal}
          title={t('checkout.step3.upperSection.rightPart.total')}
        />
        {!hideVoucher && voucherValue?.code && (
          <Row>
            <Col className={styles.subtitle} {...columnSpans.left}>
              <p className="mb-0">{t('voucher.fields.voucherCode')}</p>
              <p className="mb-0">{voucherValue.code}</p>
            </Col>
            <Col className={styles.subtitle__value} {...columnSpans.right}>
              {isLoading ? <Skeleton /> : (
                <FormatPrice
                  className="mb-0"
                  currencyUnit={currencyUnit}
                  price={voucherValue?.totalDiscount}
                  wrappedBy="p"
                />
              )}
            </Col>
          </Row>
        )}

        <RenderSubtitles
          uppercase
          currencyUnit={currencyUnit}
          hidden={hideVAT || !checkoutSettings?.includingVAT}
          isLoading={isLoading}
          price={vat}
          title={t('vat')}
        />
        <RenderSubtitles
          bold
          currencyUnit={currencyUnit}
          hidden={hideVAT || !checkoutSettings?.includingVAT}
          isLoading={isLoading}
          price={cartTotalVAT}
          title={t('totalWithVat')}
        />
      </div>
    </div>
  )
}

export default B2BecCartSummary
